import axios from 'axios';
import { useEffect, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useSnackbar } from 'notistack';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ProjectConfig } from 'Global';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import 'assets/css/main.css';
import UsersEditor from './UsersEditor';
import EmptyElement from 'elements/EmptyElement';
import TuneIcon from '@mui/icons-material/Tune';
import ListPagination from 'elements/ListPagination';
import { ApiRequest } from 'GlobalFunctions';
import LoadingButton from '@mui/lab/LoadingButton';
import Indicators from './components/Indicators';
import { FaSquareCheck, FaRegSquare, FaSquareMinus } from "react-icons/fa6";
import BadgePayment from 'elements/BadgePayment';
import BadgeFulfillment from 'elements/BadgeFulfillment';
import OrdersFilters from './components/OrdersFilters';
import ConfirmStatusChangeDialog from './components/ConfirmStatusChangeDialog';
import BulkCancelDialog from './components/BulkCancelDialog';
import { DateRange } from 'react-day-picker';

export default function OrdersList(props: any) {
  const [open, setOpen] = useState(false);
  const [filtersValues, setFiltersValues] = useState<any>([]);
  const { company_id = null, is_popup = false } = props;
  const [selectedOrders, setSelectedOrders] = useState<(string | number)[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('All Orders');
  const [bulkConfirmDialogOpen, setBulkConfirmDialogOpen] = useState(false);
  const [bulkStatusChangeLoading, setBulkStatusChangeLoading] = useState(false);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [printInvoicesLoading, setPrintInvoicesLoading] = useState(false);

  const formatPrice = (price: number | string) => {
    return Number(price).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { setLoading } = props;
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, []);


  const [list, setList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ total_pages: 1, total_items: 0 });
  const queryParams = new URLSearchParams(window.location.search);
  const currentPage = 1;
  const [page, setPage] = useState(currentPage);
  const [indicators, setIndicators] = useState({
    totalOrders: 0,
    ordersDelivered: 0,
    ordersCancelled: 0,
    totalRevenue: 0
  });
  
  const [loadingOrderId, setLoadingOrderId] = useState<string | number | null>(null); // Estado para manejar el botón en carga
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [page, dateRange, activeFilter]);

  
  const loadList = async () => {
    setLoading(true);
    
    const queryParams: any = { page, filters: filtersValues };
    
    // Add active filter to query if it's not "All Orders"
    if (activeFilter && activeFilter !== 'All Orders') {
      queryParams.status_filter = activeFilter;
    }
    
    // Add date range to query if it exists
    if (dateRange?.from) {
      queryParams.date_from = dateRange.from.toISOString();
    }
    if (dateRange?.to) {
      queryParams.date_to = dateRange.to.toISOString();
    }
    
    await ApiRequest({
      method: 'GET',
      url: '/companies/orders/list',
      query: queryParams,
      setResponse: (response: any) => {
          setList(response.list);
          setIndicators(response.indicators);
          setPagination(response.pagination);
        
      }
    })
    setLoading(false);
    
  }

  const handleRowClick = (order_id: number | string) => {
    navigate(`/orders/${order_id}`);
  };

  const handleSelectAll = (event: MouseEvent) => {
    event.stopPropagation();
    // Si hay alguna selección (parcial o completa), deseleccionar todas
    if (selectedOrders.length > 0) {
      setSelectedOrders([]);
    } else {
      // Si no hay ninguna selección, seleccionar todas
      const allOrderIds = list.map(item => item.order_id);
      setSelectedOrders(allOrderIds);
    }
  };

  const handleSelectOne = (event: MouseEvent, order_id: string | number) => {
    event.stopPropagation();
    
    const isSelected = selectedOrders.includes(order_id);
    
    if (isSelected) {
      // Remove from selection
      setSelectedOrders(selectedOrders.filter(id => id !== order_id));
    } else {
      // Add to selection
      setSelectedOrders([...selectedOrders, order_id]);
    }
  };

  const downloadInvoice = async (event: MouseEvent<HTMLButtonElement>, order_id: number | string) => {
    event.stopPropagation();
    setLoadingOrderId(order_id); // Activar loadingButton para el order_id específico
    ApiRequest({
      method: 'GET',
      url: '/companies/orders/getInvoice',
      query: { order_id },
      setResponse: (response: any) => {
        window.open(response.fileUrl, '_blank');
        setLoadingOrderId(null); // Desactivar loadingButton después de completar
      },
      onError: () => {
        setLoadingOrderId(null); // Desactivar loadingButton en caso de error
      }
    });
  }

  const handleBulkMarkAsFulfilled = () => {
    setBulkConfirmDialogOpen(true);
  };

  const handleBulkCancelOrders = () => {
    setBulkCancelDialogOpen(true);
  };

  const handleExportOrders = async () => {
    setExportLoading(true);
    
    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/exportToCSV',
      body: {
        order_ids: selectedOrders
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          // Open the CSV file in a new tab
          window.open(response.data.file_url, '_blank');
          
          // Show success message with PDF generation info
          const pdfMessage = response.data.pdfs_generated > 0 
            ? ` (${response.data.pdfs_generated} PDFs generated)`
            : '';
          enqueueSnackbar(`${response.data.total_orders} orders exported successfully${pdfMessage}`, { variant: 'success' });
          
          // Show warning if some PDFs failed
          if (response.data.pdfs_failed > 0) {
            enqueueSnackbar(`Warning: ${response.data.pdfs_failed} PDFs failed to generate`, { variant: 'warning' });
          }
        }
      },
      onError: () => {
        enqueueSnackbar('Failed to export orders', { variant: 'error' });
      }
    });
    
    setExportLoading(false);
  };

  const handlePrintInvoices = async () => {
    setPrintInvoicesLoading(true);
    
    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/bulkPrintInvoices',
      body: {
        order_ids: selectedOrders
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          // Open the ZIP file in a new tab to download it
          window.open(response.data.file_url, '_blank');
          
          enqueueSnackbar(`${response.data.total_orders} invoices generated and zipped successfully`, { variant: 'success' });
        }
      },
      onError: () => {
        enqueueSnackbar('Failed to generate invoices', { variant: 'error' });
      }
    });
    
    setPrintInvoicesLoading(false);
  };

  // Check if any selected order is non-Shippo
  const hasNonShippoOrder = () => {
    return selectedOrders.some(orderId => {
      const order = list.find(item => item.order_id === orderId);
      return order && !order.is_shippo;
    });
  };

  const confirmBulkStatusChange = async (sendNotification?: boolean, shippingCarrierCode?: string, trackingNumber?: string, customCarrier?: string) => {
    setBulkStatusChangeLoading(true);
    
    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/bulkUpdateFulfillmentStatus',
      body: {
        order_ids: selectedOrders,
        fulfillment_status_code: 'SHIPPED',
        send_notification: sendNotification || false,
        shipping_carrier_code: shippingCarrierCode,
        tracking_number: trackingNumber,
        custom_carrier: customCarrier
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setBulkConfirmDialogOpen(false);
          setBulkStatusChangeLoading(false);
          setSelectedOrders([]); // Clear selection
          loadList(); // Reload the list
          enqueueSnackbar(`${response.data.success_count} orders updated successfully`, { variant: 'success' });
          
          if (response.data.failed_count > 0) {
            enqueueSnackbar(`${response.data.failed_count} orders failed to update`, { variant: 'warning' });
          }
        }
      },
      onError: () => {
        setBulkStatusChangeLoading(false);
      }
    });
  };

  return (
    <div id='UsersList' className='ModuleContainer'>
      <div className="ModuleWrapper"> 
        <div className="boxContainerHeader">
          <div className="ComponentTitle">
              <h1>Orders</h1>
          </div>
          

          <div className="componentTools">
          </div>
        </div>

        <OrdersFilters 
          onFilterChange={(filter: string) => {
            setActiveFilter(filter);
            setPage(1); // Reset to first page when filter changes
          }}
        />

        <Indicators 
          indicators={indicators}
          dateRange={dateRange}
          onDateRangeChange={(range) => {
            setDateRange(range);
            setPage(1); // Reset to first page when date changes
          }}
        />

        <div id='UsersListIn'>
          <div>
            {list.length !== 0 ? (
              <TableContainer className="table_1">
                <Table aria-label="sticky table"> 
                  <TableHead>
                    {selectedOrders.length > 0 ? (
                      <TableRow sx={{ backgroundColor: '#F5F3FF' }}>
                        <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                          <div onClick={handleSelectAll} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <FaSquareMinus size={20} color="#7963FF" />
                          </div>
                        </TableCell>
                        <TableCell colSpan={8} sx={{ borderBottom: "none" }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '16px'
                          }}>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#000',
                              textTransform: 'none',
                              fontWeight: 500,
                            }}>
                              {selectedOrders.length} Selected
                            </span>
                            <LoadingButton 
                              variant="outlined"
                              loading={printInvoicesLoading}
                              onClick={handlePrintInvoices}
                              sx={{ 
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '14px',
                                borderColor: '#7963FF',
                                height: '30px',
                                color: '#7963FF',
                                padding: '2px 10px',
                                '&:hover': {
                                  borderColor: '#6952E0',
                                  backgroundColor: '#F5F3FF'
                                }
                              }}
                            >
                              Print Invoices
                            </LoadingButton>
                            <LoadingButton 
                              variant="outlined"
                              loading={exportLoading}
                              onClick={handleExportOrders}
                              sx={{ 
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '14px',
                                borderColor: '#7963FF',
                                padding: '2px 10px',
                                color: '#7963FF',
                                '&:hover': {
                                  borderColor: '#6952E0',
                                  backgroundColor: '#F5F3FF'
                                }
                              }}
                            >
                              Export Orders
                            </LoadingButton>
                            {/* <Button 
                              variant="outlined" 
                              onClick={handleBulkCancelOrders}
                              sx={{ 
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '14px',
                                borderColor: '#7963FF',
                                padding: '2px 10px',
                                color: '#7963FF',
                                '&:hover': {
                                  borderColor: '#6952E0',
                                  backgroundColor: '#F5F3FF'
                                }
                              }}
                            >
                              Cancel Orders
                            </Button> */}
                            {/* <Button 
                              variant="outlined" 
                              onClick={handleBulkMarkAsFulfilled}
                              sx={{ 
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '14px',
                                borderColor: '#7963FF',
                                padding: '2px 10px',
                                color: '#7963FF',
                                '&:hover': {
                                  borderColor: '#6952E0',
                                  backgroundColor: '#F5F3FF'
                                }
                              }}
                            >
                              Mark as Fulfilled
                            </Button> */}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                          <div onClick={handleSelectAll} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} className="td_checkbox">
                            {selectedOrders.length === 0 ? (
                              <FaRegSquare size={20} color="#000" />
                            ) : selectedOrders.length === list.length ? (
                              <FaSquareCheck size={20} color="#7963FF" />
                            ) : (
                              <FaSquareMinus size={20} color="#7963FF" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Order</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Date</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Customer</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Total</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Items</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Payment Status</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Fulfillment Status</TableCell>
                        <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>Invoice (PDF)</TableCell>
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {list.map((item, i) => (
                      <TableRow
                        key={i}
                        hover
                        role="checkbox"
                        onClick={() => handleRowClick(item.order_id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell className="td_checkbox" padding="checkbox">
                          <div 
                            onClick={(event) => handleSelectOne(event, item.order_id)} 
                            style={{ cursor: 'pointer' }}
                            className="td_checkbox"
                          >
                            {selectedOrders.includes(item.order_id) ? (
                              <FaSquareCheck size={20} color="#7963FF"/>
                            ) : (
                              <FaRegSquare size={20} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell># {item.order_id}</TableCell>
                        <TableCell>{item.created_at}</TableCell>
                        <TableCell>{item.client_name}</TableCell>
                        <TableCell>${formatPrice(item.total_price)}</TableCell>
                        <TableCell>{item.total_products}</TableCell>
                        <TableCell>
                          <BadgePayment id={item.payment_status_id} />
                        </TableCell>
                        <TableCell>
                          <BadgeFulfillment code={item.fulfillment_status_code} />
                        </TableCell>
                        <TableCell>
                          <LoadingButton
                            variant="outlined"
                            color="primary"
                            className='invoice_button'
                            loading={loadingOrderId === item.order_id} // Mostrar loading solo si el order_id coincide
                            onClick={(event) => downloadInvoice(event, item.order_id)}
                          >
                            Invoice
                          </LoadingButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <div className="empty_orders_wrapper">
                <span className="empty_orders_title">No results found for your current filters.</span>
                <Button
                  variant="contained"
                  onClick={() => {
                    setFiltersValues([]);
                    setActiveFilter('All Orders');
                    setDateRange(undefined);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: '100px',
                    textTransform: 'none',
                    marginTop: '16px',
                    width: '250px',
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            )}
            {pagination.total_pages > 1 && (
              <ListPagination 
                pagination={pagination} 
                page={page}  
                onChange={(event: any, value: any) => setPage(value)} 
                label="Orders"
                style={{ marginTop: "20px", alignSelf: "center" }}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmStatusChangeDialog
        open={bulkConfirmDialogOpen}
        setOpen={setBulkConfirmDialogOpen}
        loading={bulkStatusChangeLoading}
        oldStatus="ORDER_RECEIVED"
        newStatus="SHIPPED"
        onConfirm={confirmBulkStatusChange}
        isShippo={!hasNonShippoOrder()}
      />

      <BulkCancelDialog
        open={bulkCancelDialogOpen}
        setOpen={setBulkCancelDialogOpen}
        loading={bulkCancelLoading}
        selectedOrders={selectedOrders}
        loadList={loadList}
        onSuccess={(response: any) => {
          setSelectedOrders([]);
          enqueueSnackbar(`${response.data.success_count} orders cancelled successfully`, { variant: 'success' });
          if (response.data.failed_count > 0) {
            enqueueSnackbar(`${response.data.failed_count} orders failed to cancel`, { variant: 'warning' });
          }
        }}
      />
      
    </div>
  );
};
